import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Star, MapPin, Globe, Briefcase, Clock, Wifi, Users,
  ChevronLeft, MessageSquare, ExternalLink, Sparkles, Award, Languages,
} from "lucide-react";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import EliteBadge from "../components/EliteBadge";
import SeoHead from "../components/SeoHead";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, getAccessToken } from "../services/api";

const CONTRACT_OPTIONS = ["Freelance", "CDI", "Mission"];
const REMOTE_OPTIONS = [
  { v: "remote", label: "Télétravail" },
  { v: "onsite", label: "Présentiel" },
  { v: "both", label: "Les deux" },
];
const AVAILABILITY_OPTIONS = ["Immédiate", "1 mois", "3 mois", "Non disponible"];

const PAGE_TITLE = "AFRICTAL Elite Talents — Vitrine panafricaine des talents d'excellence";
const PAGE_DESC =
  "Découvrez les Talents et Freelances africains labellisés AFRICTAL Elite : profils vérifiés, compétences validées, disponibilité en temps réel. Recrutez l'excellence panafricaine.";
const OG_IMAGE = "/brand/africtal-logo.png";

/** Inject/update SEO head tags — délégué au composant SeoHead (react-helmet-async).
 *  Conservé pour compat backward avec les deux composants ci-dessous ; en pratique
 *  on émet directement un <SeoHead ... /> dans le JSX de chaque écran.
 */
function useSeo({ title, description, url, imageUrl, jsonLd }) {  // eslint-disable-line no-unused-vars
  // No-op runtime : le composant utilise désormais <SeoHead /> dans son JSX.
  // Fonction conservée pour éviter les refactos massifs si d'autres modules
  // référencent l'ancien hook.
}

function AvatarBubble({ name, url, size = 46 }) {
  if (url) {
    return (
      <img
        src={url}
        alt={name || "Profil"}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", background: "#eee" }}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: "50%",
        background: "linear-gradient(135deg,#009640,#0F2A42)",
        color: "#fff", display: "grid", placeItems: "center", fontWeight: 700,
        fontSize: size >= 64 ? 28 : 18,
      }}
    >
      {(name || "?").charAt(0).toUpperCase()}
    </div>
  );
}

function PublicShell({ children, testid }) {
  return (
    <div className="afr-root" data-testid={testid || "elite-showcase-root"}>
      <PublicNav />
      <main style={{ padding: "2rem 1.4rem", maxWidth: 1200, margin: "0 auto" }}>
        {children}
      </main>
      <PublicFooter />
    </div>
  );
}

/**
 * EliteShowcase — Page publique /elite (V3.3 · additif)
 * Vitrine panafricaine des membres AFRICTAL Elite Talent. Feature flag
 * showcase_enabled (Super Admin). Chaque membre choisit son opt-in.
 * Filtres recruteur combinables · SEO first · Schema.org.
 */
export default function EliteShowcase() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    country: "", domain: "", metier: "",
    skills: "", languages: "",
    availability: "", remote: "", contract_type: "",
  });
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  const isAdmin = ["admin", "super-admin"].includes(user?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await api.get("/v1/elite/showcase", { params });
      setItems(data.items || []);
      setEnabled(Boolean(data.enabled));
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const setF = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));
  const clearFilters = () => setFilters({ country: "", domain: "", metier: "", skills: "", languages: "", availability: "", remote: "", contract_type: "" });
  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  const jsonLd = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: PAGE_TITLE,
    description: PAGE_DESC,
    url: typeof window !== "undefined" ? window.location.href : "",
    isPartOf: {
      "@type": "WebSite",
      name: "AFRICTAL",
      url: typeof window !== "undefined" ? window.location.origin : "",
    },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: items.length,
      itemListElement: items.slice(0, 20).map((m, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Person",
          name: m.name,
          jobTitle: m.metier || m.headline || m.role,
          address: m.country ? { "@type": "PostalAddress", addressCountry: m.country } : undefined,
          knowsAbout: m.skills || [],
        },
      })),
    },
  }), [items]);

  useSeo({
    title: PAGE_TITLE,
    description: PAGE_DESC,
    url: typeof window !== "undefined" ? window.location.href : undefined,
    jsonLd,
  });
  const eliteListingSeo = (
    <SeoHead
      title="Elite Talents"
      description={PAGE_DESC}
      path="/elite"
      jsonLd={jsonLd}
    />
  );

  const goContact = (uid) => {
    if (!user || !getAccessToken()) {
      toast.info("Connectez-vous pour contacter ce talent AFRICTAL Elite.");
      navigate(`/login?next=${encodeURIComponent(`/messages?to=${uid}`)}`);
      return;
    }
    navigate(`/messages?to=${uid}`);
  };

  // ─────────────────────────────────────────────────────────────────────
  // État "vitrine désactivée" : page pro accessible, aucun profil listé.
  // ─────────────────────────────────────────────────────────────────────
  if (!enabled && !isAdmin && !loading) {
    return (
      <PublicShell testid="showcase-not-activated-shell">
        {eliteListingSeo}
        <section className="afr-hero" data-testid="showcase-not-activated" style={{ paddingTop: "3rem" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, padding: "10px 18px", borderRadius: 999, background: "linear-gradient(135deg,#F59E0B,#B45309)", color: "#fff", fontWeight: 700, marginBottom: 20 }}>
            <Star size={16} fill="#fff" /> AFRICTAL Elite Talent
          </div>
          <h1 className="afr-hero__title" style={{ marginBottom: "1rem" }}>
            La vitrine <em>arrive prochainement</em>
          </h1>
          <p className="afr-hero__lead" style={{ maxWidth: 720 }}>
            AFRICTAL Elite Talent est notre programme d'excellence panafricain — un cercle
            très exclusif de talents et freelances sélectionnés par notre comité de curation.
            La vitrine publique sera bientôt ouverte aux recruteurs.
          </p>
          <div className="afr-flex-row" style={{ marginTop: "1.6rem" }}>
            <Link to="/pour-les-employeurs" className="afr-btn afr-btn--primary afr-btn--lg" data-testid="showcase-cta-recruiters">
              <Sparkles size={16} /> Découvrir pour les recruteurs
            </Link>
            <Link to="/" className="afr-btn afr-btn--outline afr-btn--lg" data-testid="showcase-cta-home">
              Retour à l'accueil
            </Link>
          </div>
        </section>
      </PublicShell>
    );
  }

  return (
    <PublicShell>
      {eliteListingSeo}
      <header className="afr-hero" data-testid="showcase-header" style={{ paddingTop: "1rem", paddingBottom: "1.2rem" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "6px 14px", borderRadius: 999, background: "linear-gradient(135deg,#F59E0B,#B45309)", color: "#fff", fontWeight: 700, fontSize: "0.82rem", marginBottom: 14 }}>
          <Star size={14} fill="#fff" /> AFRICTAL Elite Talent
        </div>
        <h1 className="afr-hero__title" style={{ marginBottom: "0.6rem" }}>
          Vitrine <em>panafricaine</em> de l'excellence
        </h1>
        <p className="afr-hero__lead" style={{ maxWidth: 780 }}>
          Les Talents et Freelances labellisés AFRICTAL Elite représentent une sélection rigoureuse
          des meilleurs profils du continent. Recruteurs : filtrez par pays, domaine, métier,
          compétences et disponibilité — contactez directement les profils qui correspondent à
          votre besoin.
        </p>
        {isAdmin && !enabled && (
          <div data-testid="showcase-preview-banner" style={{ marginTop: 16, padding: "10px 14px", borderRadius: 12, background: "rgba(180,83,9,0.10)", color: "#B45309", fontWeight: 600, fontSize: "0.86rem", display: "inline-flex", alignItems: "center", gap: 6 }}>
            👁️ Mode aperçu Admin — la vitrine n'est pas encore activée pour le public.
          </div>
        )}
      </header>

      {/* Filtres recruteur */}
      <div className="afr-card" style={{ padding: 16, marginBottom: 20 }} data-testid="showcase-filters">
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <FilterInput label="Pays" value={filters.country} onChange={setF("country")} testid="filter-country" placeholder="Sénégal, Côte d'Ivoire…" Icon={Globe} />
          <FilterInput label="Domaine" value={filters.domain} onChange={setF("domain")} testid="filter-domain" placeholder="Fintech, EdTech…" Icon={Sparkles} />
          <FilterInput label="Métier" value={filters.metier} onChange={setF("metier")} testid="filter-metier" placeholder="Développeur Java, Designer…" Icon={Briefcase} />
          <FilterInput label="Compétences" value={filters.skills} onChange={setF("skills")} testid="filter-skills" placeholder="Python, React (CSV)" Icon={Sparkles} />
          <FilterInput label="Langues" value={filters.languages} onChange={setF("languages")} testid="filter-languages" placeholder="Français, Wolof (CSV)" Icon={Languages} />
          <FilterSelect label="Disponibilité" value={filters.availability} onChange={setF("availability")} testid="filter-availability" Icon={Clock} options={AVAILABILITY_OPTIONS.map((o) => ({ v: o, label: o }))} />
          <FilterSelect label="Télétravail" value={filters.remote} onChange={setF("remote")} testid="filter-remote" Icon={Wifi} options={REMOTE_OPTIONS} />
          <FilterSelect label="Type de contrat" value={filters.contract_type} onChange={setF("contract_type")} testid="filter-contract" Icon={Briefcase} options={CONTRACT_OPTIONS.map((o) => ({ v: o, label: o }))} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12, flexWrap: "wrap", gap: 8 }}>
          <span className="afr-muted" style={{ fontSize: "0.85rem" }} data-testid="showcase-count">
            {items.length} talent(s) trouvé(s){activeFiltersCount ? ` · ${activeFiltersCount} filtre(s) actif(s)` : ""}
          </span>
          {activeFiltersCount > 0 && (
            <button className="afr-btn afr-btn--ghost afr-btn--sm" onClick={clearFilters} data-testid="filters-clear" type="button">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div data-testid="showcase-loading" className="afr-muted">Chargement de la vitrine…</div>
      ) : (
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))" }} data-testid="showcase-list">
          {items.length === 0 && (
            <div className="afr-empty" style={{ gridColumn: "1 / -1" }} data-testid="showcase-empty">
              <div className="afr-empty__icon"><Users size={40} /></div>
              <div className="afr-empty__title">Aucun profil ne correspond à vos critères</div>
              <div className="afr-empty__lead">Ajustez ou réinitialisez les filtres pour découvrir davantage de talents d'excellence.</div>
            </div>
          )}
          {items.map((m) => (
            <TalentCard
              key={m.user_id}
              m={m}
              onContact={() => goContact(m.user_id)}
            />
          ))}
        </div>
      )}
    </PublicShell>
  );
}

function TalentCard({ m, onContact }) {
  return (
    <article
      className="afr-card"
      data-testid={`showcase-card-${m.user_id}`}
      style={{ borderTop: "3px solid #F59E0B", padding: 18, display: "flex", flexDirection: "column", gap: 12 }}
    >
      <header style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <AvatarBubble name={m.name} url={m.avatar_url} size={52} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <strong style={{ fontSize: "1.02rem", color: "var(--afr-secondary)" }}>{m.name}</strong>
            <EliteBadge size="sm" />
          </div>
          <div className="afr-muted" style={{ fontSize: "0.86rem", marginTop: 2 }}>
            {m.metier || m.headline || (m.role === "freelance" ? "Freelance" : "Talent")}
          </div>
        </div>
      </header>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: "0.82rem", color: "#475569" }}>
        {m.country && (
          <span data-testid={`card-country-${m.user_id}`}>
            <MapPin size={12} style={{ verticalAlign: "middle" }} /> {m.country}
          </span>
        )}
        {m.availability && (
          <span data-testid={`card-availability-${m.user_id}`}>
            <Clock size={12} style={{ verticalAlign: "middle" }} /> {m.availability}
          </span>
        )}
        {typeof m.years_experience === "number" && (
          <span data-testid={`card-yrs-${m.user_id}`}>
            <Award size={12} style={{ verticalAlign: "middle" }} /> {m.years_experience} an{m.years_experience > 1 ? "s" : ""} d'expérience
          </span>
        )}
        {m.remote_preference && (
          <span data-testid={`card-remote-${m.user_id}`}>
            <Wifi size={12} style={{ verticalAlign: "middle" }} /> {labelRemote(m.remote_preference)}
          </span>
        )}
      </div>

      {(m.skills || []).length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }} data-testid={`card-skills-${m.user_id}`}>
          {(m.skills || []).slice(0, 3).map((s) => (
            <span key={s} className="afr-tag-chip">{s}</span>
          ))}
        </div>
      )}

      {(m.languages || []).length > 0 && (
        <div style={{ fontSize: "0.8rem", color: "#475569" }} data-testid={`card-langs-${m.user_id}`}>
          <Languages size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
          {m.languages.slice(0, 4).join(" · ")}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: "auto", flexWrap: "wrap" }}>
        <Link
          className="afr-btn afr-btn--outline afr-btn--sm"
          to={`/elite/${m.user_id}`}
          data-testid={`showcase-view-${m.user_id}`}
        >
          <ExternalLink size={12} /> Voir le profil
        </Link>
        <button
          type="button"
          className="afr-btn afr-btn--primary afr-btn--sm"
          onClick={onContact}
          data-testid={`showcase-contact-${m.user_id}`}
        >
          <MessageSquare size={12} /> Contacter
        </button>
      </div>
    </article>
  );
}

function labelRemote(v) {
  const map = { remote: "Télétravail", onsite: "Présentiel", both: "Hybride" };
  return map[v] || v;
}

function FilterInput({ label, value, onChange, testid, placeholder, Icon }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 4, fontWeight: 700, textTransform: "uppercase" }}>
        {Icon && <Icon size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />}{label}
      </label>
      <input className="afr-input" value={value} onChange={onChange} placeholder={placeholder} data-testid={testid} />
    </div>
  );
}

function FilterSelect({ label, value, onChange, testid, Icon, options }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, color: "#64748b", marginBottom: 4, fontWeight: 700, textTransform: "uppercase" }}>
        {Icon && <Icon size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />}{label}
      </label>
      <select className="afr-input" value={value} onChange={onChange} data-testid={testid}>
        <option value="">Tous</option>
        {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
      </select>
    </div>
  );
}

/**
 * EliteShowcaseProfile — détail public d'un membre Elite (route /elite/:userId).
 */
export function EliteShowcaseProfile() {
  const { userId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get(`/v1/elite/showcase/${userId}`);
        setData(d);
      } catch (_e) {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [userId]);

  const jsonLd = useMemo(() => {
    if (!data) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Person",
      name: data.user.name,
      jobTitle: data.profile?.headline || data.user.role,
      address: data.profile?.country
        ? { "@type": "PostalAddress", addressCountry: data.profile.country }
        : undefined,
      knowsAbout: data.profile?.skills || [],
      knowsLanguage: data.profile?.languages || [],
      award: "AFRICTAL Elite Talent",
    };
  }, [data]);

  useSeo({
    title: data ? `${data.user.name} · AFRICTAL Elite Talent` : "Profil Elite · AFRICTAL",
    description: data
      ? `${data.user.name} — ${data.profile?.headline || data.user.role} · Talent labellisé AFRICTAL Elite.`
      : "Découvrez ce membre AFRICTAL Elite Talent.",
    jsonLd,
  });
  const profileSeo = (
    <SeoHead
      title={data ? `${data.user.name} — Elite Talent` : "Profil Elite"}
      description={
        data
          ? `${data.user.name} — ${data.profile?.headline || data.user.role} · Talent labellisé AFRICTAL Elite.`
          : "Découvrez ce membre AFRICTAL Elite Talent."
      }
      path={userId ? `/elite/${userId}` : "/elite"}
      type="profile"
      image={data?.profile?.avatar_url}
      jsonLd={jsonLd}
    />
  );

  const contact = () => {
    if (!user || !getAccessToken()) {
      toast.info("Connectez-vous pour contacter ce talent AFRICTAL Elite.");
      navigate(`/login?next=${encodeURIComponent(`/messages?to=${userId}`)}`);
      return;
    }
    navigate(`/messages?to=${userId}`);
  };

  if (loading) {
    return (
      <PublicShell testid="showcase-profile-loading-shell">
        {profileSeo}
        <div className="afr-muted" data-testid="showcase-profile-loading">Chargement…</div>
      </PublicShell>
    );
  }

  if (notFound) {
    return (
      <PublicShell testid="showcase-profile-notfound-shell">
        {profileSeo}
        <div className="afr-empty" data-testid="showcase-profile-not-found">
          <div className="afr-empty__title">Ce profil n'est pas disponible</div>
          <Link to="/elite" className="afr-btn afr-btn--primary afr-btn--sm" style={{ marginTop: 12 }}>
            <ChevronLeft size={12} /> Retour à la vitrine
          </Link>
        </div>
      </PublicShell>
    );
  }

  const { user: u, profile, portfolio, awarded_at } = data;
  return (
    <PublicShell testid="showcase-profile-shell">
      {profileSeo}
      <div style={{ marginBottom: 14 }}>
        <Link to="/elite" className="afr-btn afr-btn--ghost afr-btn--sm" data-testid="showcase-profile-back">
          <ChevronLeft size={14} /> Retour à la vitrine
        </Link>
      </div>

      <div className="afr-card" style={{ padding: 22, marginBottom: 18 }} data-testid="showcase-profile-detail">
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
          <AvatarBubble name={u.name} url={profile?.avatar_url} size={88} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <h1 style={{ margin: 0, fontSize: "1.6rem" }}>{u.name}</h1>
              <EliteBadge />
            </div>
            <div className="afr-muted" style={{ fontSize: "0.98rem", marginTop: 6 }}>
              {profile?.headline || profile?.metier || u.role} · {profile?.country || profile?.location || "—"}
            </div>
            <div className="afr-muted" style={{ fontSize: "0.78rem", marginTop: 6 }}>
              Membre AFRICTAL Elite depuis {new Date(awarded_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
            </div>
            <div style={{ marginTop: 16 }}>
              <button
                type="button"
                className="afr-btn afr-btn--primary"
                onClick={contact}
                data-testid="showcase-profile-contact"
              >
                <MessageSquare size={14} /> Contacter
              </button>
            </div>
          </div>
        </div>

        {profile?.bio && (
          <p style={{ marginTop: 20, whiteSpace: "pre-wrap", color: "#334155", lineHeight: 1.6 }}>{profile.bio}</p>
        )}

        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", marginTop: 18, gap: 12 }}>
          {profile?.availability && (
            <InfoBlock label="Disponibilité" value={profile.availability} />
          )}
          {profile?.remote_preference && (
            <InfoBlock label="Mode de travail" value={labelRemote(profile.remote_preference)} />
          )}
          {typeof profile?.years_experience === "number" && (
            <InfoBlock label="Expérience" value={`${profile.years_experience} an${profile.years_experience > 1 ? "s" : ""}`} />
          )}
          {(profile?.contract_types || []).length > 0 && (
            <InfoBlock label="Types de contrat" value={profile.contract_types.join(" · ")} />
          )}
        </div>

        {(profile?.skills || []).length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Compétences</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {profile.skills.map((s) => <span key={s} className="afr-tag-chip">{s}</span>)}
            </div>
          </div>
        )}
        {(profile?.languages || []).length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Langues</div>
            <div>{profile.languages.join(" · ")}</div>
          </div>
        )}
      </div>

      {(portfolio || []).length > 0 && (
        <div>
          <h2 style={{ marginBottom: 10, fontSize: "1.15rem" }}>Portfolio ({portfolio.length})</h2>
          <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))" }}>
            {portfolio.map((p, i) => (
              <article key={p.id || i} className="afr-card" style={{ padding: 14 }} data-testid={`showcase-portfolio-${i}`}>
                <strong>{p.title || "Projet"}</strong>
                <div className="afr-muted" style={{ fontSize: "0.86rem", marginTop: 4 }}>{p.description || ""}</div>
              </article>
            ))}
          </div>
        </div>
      )}
    </PublicShell>
  );
}

function InfoBlock({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: "0.95rem", color: "var(--afr-secondary)" }}>{value}</div>
    </div>
  );
}
